import { makeAutoObservable } from 'mobx'
import { TooltipLink } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import type {
  IResponseEventCallerIdVerificationResult,
  IResponseNumber,
  IResponseNumberable,
} from 'entities/Phone/api/types'
import type { IResponseEventInboxCallingNumber } from 'entities/Inbox/api/types'
import { PhoneLandline } from 'entities/Phone/model/PhoneLandline'
import { BillingPath } from 'entities/ExportRouters'
import { type IEntitiesStore } from 'entities/EntitiesExport'

export class Phone {
  id: number
  formatted_number!: string
  national_number!: string
  number!: string
  is_toll_free!: boolean
  is_local!: boolean
  is_landline!: boolean
  is_mobile!: boolean
  use_organization_call_settings!: boolean
  voice_outbound!: boolean
  voice!: boolean
  conference_calling!: boolean
  verified_status!: string
  short_code!: number
  is_aircall!: boolean
  origin!: IResponseNumber
  type!: IResponseNumber['type']
  number_vendor!: IResponseNumber['number_vendor']
  country!: IResponseNumber['country']
  number_caller_id!: IResponseNumber['number_caller_id'] | null
  numberable?: IResponseNumberable | null
  disabled?: string | null
  private _hasProfile!: IResponseNumber['has_profile']
  private _landline: PhoneLandline | null = null
  private _is_zipwhip: IResponseNumber['is_zipwhip'] = 0

  constructor(item: IResponseNumber, private _entities: IEntitiesStore) {
    this.id = item.id
    this.syncOrigin(item)

    makeAutoObservable(this)
  }

  syncOrigin = (origin: IResponseNumber) => {
    this.id = origin.id
    this.formatted_number = origin.formatted_number
    this.national_number = origin.national_number
    this.number = origin.number
    this.is_toll_free = origin.is_toll_free
    this.use_organization_call_settings = origin.use_organization_call_settings
    this.voice_outbound = origin.voice_outbound
    this.voice = origin.voice
    this.conference_calling = origin.conference_calling
    this.verified_status = origin.verified_status
    this.short_code = origin.short_code
    this.is_aircall = origin.is_aircall
    this.origin = origin
    this.number_vendor = origin.number_vendor
    this.country = origin.country
    this.type = origin.type
    this.is_local = origin.is_local
    this.is_mobile = origin.is_mobile
    this.number_caller_id = origin.number_caller_id
    this.numberable = origin.numberable
    this.is_landline = !!origin.is_landline
    this._hasProfile = origin.has_profile
    this._is_zipwhip = origin.is_zipwhip
    this._landline = origin.landline ? new PhoneLandline(origin.landline, this) : null
    this.disabled = origin.disabled
  }

  get is_zipwhip() {
    return this._is_zipwhip
  }

  get landline() {
    return this._landline
  }

  get numberableName() {
    return this.numberable?.name
  }

  get isPFT() {
    return this.number_vendor.key === 'inteliquent'
  }

  get isTollFree() {
    return this.is_toll_free
  }

  get isLocal() {
    return this.is_local
  }

  get isLandline() {
    return this.is_landline
  }
  get isZipwhip() {
    return this.origin.is_landline && this.origin.landline && this.origin.is_zipwhip
  }

  get isStatusUnverified() {
    return this.verified_status === 'Unverified'
  }

  get isStatusDeclined() {
    return this.verified_status === 'Denied'
  }

  get isStatusBlocked() {
    return this.verified_status === 'Blocked'
  }

  get isStatusVerified() {
    return this.verified_status === 'Verified'
  }

  get isStatusUnderVerification() {
    return this.verified_status === 'Under Review'
  }

  get isStatusInternalReview() {
    return this.verified_status === 'Internal Review'
  }

  get isConferenceCalling() {
    return this.conference_calling
  }

  get isUseOrganizationCallSettings() {
    return this.use_organization_call_settings
  }

  get isShortCode() {
    return Boolean(this.short_code)
  }

  get hasProfile() {
    return this._hasProfile
  }

  get isNotVerifiedCallerId() {
    if (
      this.number_vendor.key === 'twilio' ||
      this.number_vendor.key === 'twilio-integration' ||
      this.number_vendor.key === 'aircall-api'
    ) {
      return false
    }

    return !this.number_caller_id || this.number_caller_id?.verified === false
  }

  get callerIdFailedReason() {
    if (this.isNotVerifiedCallerId) {
      return 'Caller ID is being verified. Please contact support if it takes longer than 1 hour.'
    }

    return null
  }

  updateInboxCalling = (data: IResponseEventInboxCallingNumber) => {
    this.use_organization_call_settings = data.use_organization_call_settings
    this.voice_outbound = data.voice_outbound
    this.voice = data.voice
  }

  updateNumberCallerId = (data: IResponseEventCallerIdVerificationResult) => {
    const caller_id_sid = this.number_caller_id?.caller_id_sid || null
    const failed_reason = this.number_caller_id?.failed_reason || null

    this.number_caller_id = {
      caller_id_sid,
      verified: data.verificationResult,
      failed_reason,
    }
  }

  get status() {
    if (this.isTollFree) {
      return this.origin.verified_status?.toLowerCase()
    }

    if (this.isLandline) {
      const landlineStatus = this.origin.landline?.status
      if (landlineStatus === 'completed') return 'verifiedLandline'
      if (landlineStatus === 'failed') return 'failed'
      return 'pending'
    }

    return ''
  }

  get isMobile() {
    return this.type === 'Mobile' || this.is_mobile
  }

  get labelType() {
    if (this.isMobile) {
      return 'Mobile'
    }

    if (this.isLandline) {
      return 'Landline'
    }

    if (this.isZipwhip) {
      return 'ZipWhip'
    }

    if (this.isTollFree) {
      return 'Toll-Free'
    }

    if (this.is_aircall) {
      return 'Aircall'
    }

    if (this.isShortCode) {
      return 'Short Code'
    }

    return 'Local'
  }

  get address() {
    const { city, state, area_code } = this.origin
    let address = undefined
    if (city && state) {
      address = `${city}, ${state}`
    }
    if (!address && area_code?.state_name && area_code?.state_code) {
      address = `${area_code.state_name}, ${area_code.state_code}`
    }
    return address
  }

  get tooltip() {
    const { type, numberable } = this.origin
    return type === 'Short Code' && numberable?.name ? numberable.name : undefined
  }

  get lock() {
    if (this._entities.subscriptionStore.isPAYGPlan) {
      return {
        tooltip: (
          <>
            You have reached the limit of 1 number on Pay <br />
            As You Go plan. Upgrade to remove the limits. <br />
            <TooltipLink
              text={'Upgrade'}
              onClick={() => {
                uiStore.changeRouteViaVue({
                  path: `/${BillingPath.upgrade}`,
                })
              }}
            />
          </>
        ),
      }
    }

    return false
  }

  get itemInbox() {
    if (this.origin.numberable_type === 'App\\Numbers\\Shortcode') {
      return this.origin.numberable && this.origin.numberable.team
        ? this.origin.numberable?.team
        : null
    }

    return this.origin.numberable
  }

  get isAssigned() {
    return Boolean(this.origin.numberable_id && this.origin.numberable)
  }

  get isMultiNumber() {
    return Boolean(this.origin.team_numbers_count && this.origin.team_numbers_count > 1)
  }
}
